import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  EndOfDayReportData,
  reportsService,
} from "../../services/reports.service";
import { storeService } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { formatBusinessDate } from "../../lib/utils";

export default function EndOfDayReconciliationPage() {
  const { selectedStoreId } = useAuthStore();
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [filters, setFilters] = useState({
    date: formatBusinessDate(new Date()),
    storeId: selectedStoreId || "",
  });
  const [reportData, setReportData] = useState<EndOfDayReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    storeService
      .getAll()
      .then(setStores)
      .catch(() => {});
  }, []);

  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getEndOfDayReport({
        date: filters.date,
        storeId: filters.storeId || undefined,
      });
      setReportData(data);
    } catch (error) {
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          End-of-day Reconciliation
        </h1>
        <p className="text-gray-600">
          Cash reconciliation summary by billing session
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store
            </label>
            <select
              value={filters.storeId}
              onChange={(e) =>
                setFilters({ ...filters, storeId: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={loadReport}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(reportData.summary.totalSales)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Expected Cash</p>
              <p className="text-xl font-semibold text-blue-600">
                {formatCurrency(reportData.summary.expectedCash)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Actual Cash</p>
              <p className="text-xl font-semibold text-emerald-600">
                {formatCurrency(reportData.summary.actualCash)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Variance</p>
              <p className="text-xl font-semibold text-red-600">
                {formatCurrency(reportData.summary.variance)}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Sessions</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2">Session</th>
                    <th className="text-left py-2">Counter</th>
                    <th className="text-right py-2">Opening</th>
                    <th className="text-right py-2">Expected</th>
                    <th className="text-right py-2">Actual</th>
                    <th className="text-right py-2">Variance</th>
                    <th className="text-right py-2">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.sessions.map((session) => (
                    <tr key={session.sessionId} className="border-b">
                      <td className="py-2">
                        <Link
                          to={`/billing-sessions/${session.sessionId}`}
                          className="text-blue-600 hover:underline"
                        >
                          {session.sessionNumber}
                        </Link>
                      </td>
                      <td className="py-2">{session.counterName}</td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.openingCash)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.expectedCash)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.actualCash)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.variance)}
                      </td>
                      <td className="py-2 text-right">
                        {session.invoiceCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
